// apps/web/src/components/merch/CatalogProductForm.tsx
import { useState, type ReactNode } from 'react'

export interface CatalogVariantForm {
  id?: string
  label: string
  size?: string
  colour?: string
  sku?: string
  pricePence?: number | null
  inventory: number
}

export interface CatalogItemForm {
  name: string
  sku: string
  pricePence: number
  description: string
  status: 'draft' | 'active' | 'archived'
  tags: string
  imageUrl: string
  madeToOrder: boolean
  variants: CatalogVariantForm[]
}

const emptyVariant = (): CatalogVariantForm => ({
  label: 'Default',
  inventory: 0,
})

export function blankCatalogForm(): CatalogItemForm {
  return {
    name: '',
    sku: '',
    pricePence: 0,
    description: '',
    status: 'draft',
    tags: '',
    imageUrl: '',
    madeToOrder: false,
    variants: [emptyVariant()],
  }
}

interface Props {
  initial?: CatalogItemForm
  submitting?: boolean
  onSubmit: (form: CatalogItemForm) => void
  onCancel: () => void
}

export function CatalogProductForm({ initial, submitting, onSubmit, onCancel }: Props) {
  const [form, setForm] = useState<CatalogItemForm>(initial ?? blankCatalogForm())

  return (
    <form
      className="space-y-4 rounded-lg border border-ovation-ink/10 bg-ovation-ink/[0.02] p-4"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit(form)
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name">
          <input
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="SKU">
          <input
            required
            value={form.sku}
            onChange={(e) => setForm({ ...form, sku: e.target.value })}
            className="w-full rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Price (pence)">
          <input
            type="number"
            min={0}
            required
            value={form.pricePence}
            onChange={(e) => setForm({ ...form, pricePence: Number(e.target.value) || 0 })}
            className="w-full rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Status">
          <select
            value={form.status}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value as CatalogItemForm['status'] })
            }
            className="w-full rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </Field>
      </div>

      <Field label="Description">
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3}
          className="w-full rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
        />
      </Field>

      <Field label="Tags (comma-separated)">
        <input
          value={form.tags}
          onChange={(e) => setForm({ ...form, tags: e.target.value })}
          className="w-full rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
        />
      </Field>

      <Field label="Image URL">
        <input
          value={form.imageUrl}
          onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
          placeholder="https://…"
          className="w-full rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
        />
      </Field>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.madeToOrder}
          onChange={(e) => setForm({ ...form, madeToOrder: e.target.checked })}
        />
        Made to order (allow oversell)
      </label>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-ovation-muted">
            Variants
          </p>
          <button
            type="button"
            className="text-xs text-ovation-ink underline"
            onClick={() =>
              setForm({ ...form, variants: [...form.variants, emptyVariant()] })
            }
          >
            Add variant
          </button>
        </div>
        <div className="space-y-2">
          {form.variants.map((v, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-4">
              <input
                placeholder="Label"
                value={v.label}
                onChange={(e) => {
                  const variants = [...form.variants]
                  variants[i] = { ...v, label: e.target.value }
                  setForm({ ...form, variants })
                }}
                className="rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
              />
              <input
                placeholder="SKU"
                value={v.sku ?? ''}
                onChange={(e) => {
                  const variants = [...form.variants]
                  variants[i] = { ...v, sku: e.target.value }
                  setForm({ ...form, variants })
                }}
                className="rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
              />
              <input
                type="number"
                placeholder="Inventory"
                value={v.inventory}
                onChange={(e) => {
                  const variants = [...form.variants]
                  variants[i] = { ...v, inventory: Number(e.target.value) || 0 }
                  setForm({ ...form, variants })
                }}
                className="rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
              />
              <button
                type="button"
                className="text-xs text-red-600"
                onClick={() =>
                  setForm({
                    ...form,
                    variants: form.variants.filter((_, idx) => idx !== i),
                  })
                }
                disabled={form.variants.length <= 1}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="rounded bg-ovation-ink px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {submitting ? 'Saving…' : 'Save product'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-ovation-ink/15 px-3 py-1.5 text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs text-ovation-muted">{label}</span>
      {children}
    </label>
  )
}
